import React, { useEffect, useState } from "react";
import { ClipboardList, ListChecks, Settings, CheckCircle2, Clock, AlertTriangle, Plus, Trash2, CalendarPlus, Loader2, Save, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { compromisoAdminApi, apiError, isAdmin } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const STUDENT_PROGRAMS = ["Supera Mini", "Supera Kids", "Supera Plus", "Supera High", "Supera Universitario"];
const TABS = [["seg", "Seguimiento", ListChecks], ["tpl", "Plantillas", ClipboardList], ["bcast", "Recordatorios", Megaphone], ["cfg", "Mensajes y permisos", Settings]];

export default function CompromisosAdmin() {
  const [tab, setTab] = useState("seg");
  return (
    <div data-testid="compromisos-admin">
      <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><ClipboardList className="w-6 h-6 text-[#E9A400]" /> Hoja de Compromisos Supera</h1>
      <p className="text-sm text-[#6B7688] mt-1">Seguimiento por usuario, plantillas por programa, mensajes y permisos del staff.</p>
      <div className="mt-5 flex gap-1 border-b border-black/5 overflow-x-auto">
        {TABS.map(([v, l, Ic]) => (
          <button key={v} data-testid={`compadm-tab-${v}`} onClick={() => setTab(v)} className={`px-4 py-2.5 font-bold text-sm -mb-px border-b-2 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${tab === v ? "border-[var(--gold)] text-[#0A1C30]" : "border-transparent text-[#6B7688] hover:text-[#0A1C30]"}`}><Ic className="w-4 h-4" /> {l}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "seg" && <SeguimientoTab />}
        {tab === "tpl" && <PlantillasTab />}
        {tab === "bcast" && <BroadcastTab />}
        {tab === "cfg" && <ConfigTab />}
      </div>
    </div>
  );
}

function SeguimientoTab() {
  const [prog, setProg] = useState("all");
  const [data, setData] = useState(null);
  const [extend, setExtend] = useState(null);
  const load = () => compromisoAdminApi.status(prog === "all" ? undefined : prog).then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [prog]);
  const cols = data ? [
    ["Cumplieron", data.cumplidos, "#22A860", CheckCircle2],
    ["Pendientes (recordando)", data.pendientes, "#E9A400", Clock],
    ["Morosos", data.morosos, "#C0392B", AlertTriangle],
  ] : [];
  return (
    <div>
      <div className="mb-4 w-64">
        <Select value={prog} onValueChange={setProg}><SelectTrigger data-testid="seg-prog"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los programas</SelectItem>{STUDENT_PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
      </div>
      {!data ? <Loader2 className="w-6 h-6 animate-spin text-[#E9A400]" /> : (
        <div className="grid md:grid-cols-3 gap-4">
          {cols.map(([label, rows, color, Ic]) => (
            <div key={label} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
              <div className="px-4 py-3 border-b border-black/5 flex items-center gap-2" style={{ color }}><Ic className="w-4 h-4" /><span className="font-extrabold text-sm">{label}</span><span className="ml-auto text-xs bg-black/5 rounded-full px-2 py-0.5 text-[#0A1C30]">{rows.length}</span></div>
              <div className="divide-y divide-black/5 max-h-[60vh] overflow-y-auto" data-testid={`seg-col-${label}`}>
                {rows.map((r) => (
                  <div key={r.user_id} className="px-4 py-2.5">
                    <div className="flex items-center justify-between"><p className="font-bold text-[#0A1C30] text-sm">{r.nombre}</p><button data-testid={`extend-${r.user_id}`} onClick={() => setExtend(r)} title="Nueva fecha especial" className="text-[#3B7DF6] p-1"><CalendarPlus className="w-4 h-4" /></button></div>
                    <p className="text-xs text-[#6B7688]">{r.programa} · {r.pais || "—"} · Ref {r.referencia || "—"}</p>
                    <div className="mt-1 h-1.5 rounded-full bg-[#EDF0F4] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.progress}%`, background: color }} /></div>
                    {r.vencidas > 0 && <p className="text-[11px] text-[#C0392B] font-bold mt-1">{r.vencidas} hoja(s) vencida(s)</p>}
                  </div>
                ))}
                {rows.length === 0 && <p className="text-xs text-[#6B7688] p-4">Sin usuarios.</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      {extend && <ExtendDialog row={extend} weekStart={data?.week_start} onClose={() => setExtend(null)} onDone={() => { setExtend(null); load(); }} />}
    </div>
  );
}

function ExtendDialog({ row, weekStart, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [motivo, setMotivo] = useState("");
  const save = async () => {
    if (!date) { toast.error("Elige la nueva fecha."); return; }
    try { await compromisoAdminApi.extend({ user_id: row.user_id, week_start: weekStart, new_due_date: date, motivo }); toast.success("Nueva fecha enviada al padre/madre."); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="extend-dialog">
        <DialogHeader><DialogTitle>Nueva fecha de entrega — {row.nombre}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7688]">Para situaciones especiales (enfermedad, vacaciones, etc.). Se notificará al padre/madre.</p>
        <div><Label className="mb-1 block font-bold text-xs">Nueva fecha</Label><Input data-testid="extend-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div><Label className="mb-1 block font-bold text-xs">Motivo (opcional)</Label><Input value={motivo} onChange={(e) => setMotivo(e.target.value)} /></div>
        <DialogFooter><button data-testid="extend-save" onClick={save} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5">Guardar y notificar</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlantillasTab() {
  const admin = isAdmin();
  const [tpls, setTpls] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => compromisoAdminApi.templates().then(setTpls).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const missing = STUDENT_PROGRAMS.filter((p) => !tpls.some((t) => t.programa === p));
  const create = async (p) => { try { await compromisoAdminApi.createTemplate(p); toast.success("Hoja creada."); load(); } catch (e) { toast.error(apiError(e)); } };
  if (sel) return <TemplateEditor tpl={sel} onBack={() => { setSel(null); load(); }} />;
  return (
    <div>
      {admin && missing.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">{missing.map((p) => <button key={p} onClick={() => create(p)} className="inline-flex items-center gap-1 rounded-xl bg-navy text-white font-bold px-3 py-2 text-sm"><Plus className="w-4 h-4" /> Crear hoja: {p}</button>)}</div>
      )}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="tpl-list">
        {tpls.map((t) => (
          <button key={t.id} data-testid={`tpl-${t.programa}`} onClick={() => setSel(t)} className="text-left bg-white rounded-2xl ring-1 ring-black/5 p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between"><p className="font-extrabold text-[#0A1C30]">{t.programa}</p><span className={`text-xs font-bold px-2 py-1 rounded-full ${t.visible ? "bg-[#E2F6EB] text-[#22A860]" : "bg-[#EDF0F4] text-[#6B7688]"}`}>{t.visible ? "Visible" : "Oculta"}</span></div>
            <p className="text-xs text-[#6B7688] mt-1">{t.secciones.length} secciones · {t.secciones.reduce((n, s) => n + s.actividades.length, 0)} actividades · {t.puntos} pts</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function TemplateEditor({ tpl, onBack }) {
  const [secciones, setSecciones] = useState(tpl.secciones);
  const [visible, setVisible] = useState(tpl.visible);
  const [puntos, setPuntos] = useState(tpl.puntos);
  const [saving, setSaving] = useState(false);
  const upSec = (si, patch) => setSecciones((s) => s.map((x, i) => i === si ? { ...x, ...patch } : x));
  const upAct = (si, ai, patch) => setSecciones((s) => s.map((x, i) => i === si ? { ...x, actividades: x.actividades.map((a, j) => j === ai ? { ...a, ...patch } : a) } : x));
  const addAct = (si) => upSec(si, { actividades: [...secciones[si].actividades, { nombre: "Nueva actividad", frecuencia_tipo: "daily", veces: 2, instrucciones: "" }] });
  const delAct = (si, ai) => upSec(si, { actividades: secciones[si].actividades.filter((_, j) => j !== ai) });
  const addSec = () => setSecciones((s) => [...s, { nombre: "Nueva sección", actividades: [] }]);
  const delSec = (si) => setSecciones((s) => s.filter((_, i) => i !== si));
  const save = async () => {
    setSaving(true);
    try { await compromisoAdminApi.updateTemplate(tpl.id, { visible, puntos: Number(puntos), secciones }); toast.success("Plantilla guardada."); onBack(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="tpl-editor">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-sm font-bold text-[#6B7688]">← Plantillas</button>
        <button data-testid="tpl-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
      </div>
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5 flex flex-wrap items-center gap-6 mb-4">
        <p className="font-extrabold text-[#0A1C30] text-lg">{tpl.programa}</p>
        <label className="flex items-center gap-2"><span className="text-sm font-bold">Visible para usuarios</span><Switch data-testid="tpl-visible" checked={visible} onCheckedChange={setVisible} /></label>
        <label className="flex items-center gap-2"><span className="text-sm font-bold">Puntos por hoja</span><Input data-testid="tpl-puntos" type="number" value={puntos} onChange={(e) => setPuntos(e.target.value)} className="w-20" /></label>
      </div>
      <div className="space-y-4">
        {secciones.map((sec, si) => (
          <div key={si} className="bg-white rounded-2xl ring-1 ring-black/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Input value={sec.nombre} onChange={(e) => upSec(si, { nombre: e.target.value })} className="font-extrabold" />
              <button onClick={() => delSec(si)} className="p-2 rounded-lg text-[#C0392B] hover:bg-[#FDECEC]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {sec.actividades.map((a, ai) => (
                <div key={ai} className="flex flex-wrap items-center gap-2 rounded-xl bg-[#FAF8F1] p-2">
                  <Input value={a.nombre} onChange={(e) => upAct(si, ai, { nombre: e.target.value })} className="flex-1 min-w-[200px] bg-white" />
                  <Select value={a.frecuencia_tipo} onValueChange={(v) => upAct(si, ai, { frecuencia_tipo: v })}><SelectTrigger className="w-36 bg-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Diario (7)</SelectItem><SelectItem value="times_week">Veces/semana</SelectItem><SelectItem value="weekly">Semanal (1)</SelectItem></SelectContent></Select>
                  {a.frecuencia_tipo === "times_week" && <Input type="number" min="1" max="7" value={a.veces} onChange={(e) => upAct(si, ai, { veces: Number(e.target.value) })} className="w-16 bg-white" />}
                  <button onClick={() => delAct(si, ai)} className="p-2 rounded-lg text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={() => addAct(si)} className="mt-2 text-sm font-bold text-[#3B7DF6] inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Agregar actividad</button>
          </div>
        ))}
      </div>
      <button onClick={addSec} className="mt-4 inline-flex items-center gap-1 rounded-xl bg-navy text-white font-bold px-4 py-2.5 text-sm"><Plus className="w-4 h-4" /> Agregar sección</button>
    </div>
  );
}

function BroadcastTab() {
  const [f, setF] = useState({ audiencia: "todos_estudiantes", programa: "Supera Kids", user_email: "", incluir_padres: false, titulo: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const send = async () => {
    if (!f.titulo.trim() || !f.mensaje.trim()) { toast.error("Escribe título y mensaje."); return; }
    setSending(true);
    try { const r = await compromisoAdminApi.broadcast(f); toast.success(`Enviado a ${r.destinatarios} destinatario(s).`); setF((s) => ({ ...s, titulo: "", mensaje: "" })); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="max-w-2xl bg-white rounded-2xl ring-1 ring-black/5 p-6 space-y-4" data-testid="broadcast-tab">
      <div className="rounded-xl bg-[#EDF0F4] text-[#4A5568] text-xs px-3 py-2">Envía recordatorios o avisos por notificación push + buzón a la audiencia que elijas.</div>
      <div>
        <Label className="mb-1 block font-bold text-xs">Audiencia</Label>
        <Select value={f.audiencia} onValueChange={(v) => set("audiencia", v)}><SelectTrigger data-testid="bcast-aud"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos_estudiantes">Todos los estudiantes</SelectItem>
            <SelectItem value="todos_padres">Todos los padres y madres</SelectItem>
            <SelectItem value="programa">Por programa (grupo)</SelectItem>
            <SelectItem value="usuario">Un usuario específico</SelectItem>
          </SelectContent></Select>
      </div>
      {f.audiencia === "programa" && (
        <div><Label className="mb-1 block font-bold text-xs">Programa</Label>
          <Select value={f.programa} onValueChange={(v) => set("programa", v)}><SelectTrigger data-testid="bcast-prog"><SelectValue /></SelectTrigger>
            <SelectContent>{STUDENT_PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
      )}
      {f.audiencia === "usuario" && (
        <div><Label className="mb-1 block font-bold text-xs">Correo del usuario</Label><Input data-testid="bcast-email" type="email" value={f.user_email} onChange={(e) => set("user_email", e.target.value)} placeholder="usuario@correo.com" /></div>
      )}
      {(f.audiencia === "todos_estudiantes" || f.audiencia === "programa" || f.audiencia === "usuario") && (
        <label className="flex items-center gap-2"><span className="text-sm font-bold">Incluir también a los padres/madres</span><Switch data-testid="bcast-incluir-padres" checked={f.incluir_padres} onCheckedChange={(v) => set("incluir_padres", v)} /></label>
      )}
      <div><Label className="mb-1 block font-bold text-xs">Título</Label><Input data-testid="bcast-titulo" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} /></div>
      <div><Label className="mb-1 block font-bold text-xs">Mensaje</Label><textarea data-testid="bcast-mensaje" value={f.mensaje} onChange={(e) => set("mensaje", e.target.value)} className="w-full min-h-[90px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" /></div>
      <button data-testid="bcast-send" onClick={send} disabled={sending} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-3 disabled:opacity-60">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar recordatorio</button>
    </div>
  );
}

function ConfigTab() {
  const admin = isAdmin();
  const [c, setC] = useState(null);
  useEffect(() => { compromisoAdminApi.getConfig().then(setC).catch((e) => toast.error(apiError(e))); }, []);
  if (!c) return null;
  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));
  const setPerm = (k, v) => setC((s) => ({ ...s, staff_perms: { ...s.staff_perms, [k]: v } }));
  const save = async () => {
    try {
      const payload = { aviso_reinicio: c.aviso_reinicio, msg_domingo: c.msg_domingo, msg_lunes: c.msg_lunes,
        frases_motivacion: (c.frases_motivacion || []) };
      if (admin) payload.staff_perms = c.staff_perms;
      await compromisoAdminApi.saveConfig(payload); toast.success("Guardado.");
    } catch (e) { toast.error(apiError(e)); }
  };
  const PERMS = [["ver_seguimiento", "Ver seguimiento de usuarios"], ["editar_plantillas", "Editar plantillas"], ["extender_fechas", "Proponer fechas especiales"], ["editar_mensajes", "Editar mensajes/recordatorios"], ["enviar_recordatorios", "Enviar recordatorios masivos"]];
  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6 space-y-4">
        <h2 className="font-extrabold text-[#0A1C30]">Avisos y recordatorios</h2>
        <div><Label className="mb-1 block font-bold text-xs">Aviso de reinicio semanal</Label><textarea data-testid="cfg-reinicio" value={c.aviso_reinicio} onChange={(e) => set("aviso_reinicio", e.target.value)} className="w-full min-h-[60px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" /></div>
        <div><Label className="mb-1 block font-bold text-xs">Recordatorio domingo (día de entrega)</Label><textarea value={c.msg_domingo} onChange={(e) => set("msg_domingo", e.target.value)} className="w-full min-h-[60px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" /></div>
        <div><Label className="mb-1 block font-bold text-xs">Recordatorio lunes (hoja vencida)</Label><textarea value={c.msg_lunes} onChange={(e) => set("msg_lunes", e.target.value)} className="w-full min-h-[60px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" /></div>
        <div><Label className="mb-1 block font-bold text-xs">Frases de motivación (una por línea)</Label><textarea data-testid="cfg-frases" value={(c.frases_motivacion || []).join("\n")} onChange={(e) => set("frases_motivacion", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="w-full min-h-[100px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" /></div>
      </div>
      {admin && (
        <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6 space-y-3">
          <h2 className="font-extrabold text-[#0A1C30]">Permisos del staff</h2>
          <p className="text-xs text-[#6B7688]">Define qué acciones puede realizar el staff (lo no marcado queda solo para el super admin).</p>
          {PERMS.map(([k, l]) => (
            <div key={k} className="flex items-center justify-between"><Label className="font-bold text-sm">{l}</Label><Switch data-testid={`perm-${k}`} checked={!!c.staff_perms?.[k]} onCheckedChange={(v) => setPerm(k, v)} /></div>
          ))}
        </div>
      )}
      <button data-testid="cfg-save" onClick={save} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-8 py-3">Guardar configuración</button>
    </div>
  );
}
